/**
 * @file JobCompleteSnackbar.jsx
 * @description custom snackbar message
 *
 * @copyright veriome labs, llc.  - all rights reserved.
 * This file is subject to the terms and conditions defined in
 * file 'LICENSE.md', which is part of this source code package.
 * 
 * https://notistack.com/features/customization#custom-component
 * 
 * @author don michael
 * @since Feb 04, 2026 14:33
 */
import { useState, forwardRef, useCallback } from "react"
import clsx from "clsx"
import { makeStyles } from "@mui/styles"
import { useSnackbar, SnackbarContent } from "notistack"
import Collapse from "@mui/material/Collapse"
import Paper from "@mui/material/Paper"
import Typography from "@mui/material/Typography"
import Card from "@mui/material/Card"
import CardActions from "@mui/material/CardActions"
import IconButton from "@mui/material/IconButton"
import CloseIcon from "@mui/icons-material/Close"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import Stack from '@mui/material/Stack';

import { objectHas, stringExists } from 'src/@common/utilities';

const useStyles = makeStyles(() => ({
    root: {
        "@media (min-width:600px)": {
            minWidth: "270px !important"
        }
    },
    card: {
        width: "100%",
    },
    typography: {
        color: "#000",
    },
    actionRoot: {
        padding: "8px 8px 8px 16px",
        justifyContent: "space-between"
    },
    icons: {
        marginLeft: "auto"
    },
    expand: {
        padding: "8px 8px",
        transform: "rotate(0deg)",
        color: "#000",
        transition: "all .2s"
    },
    expandOpen: {
        transform: "rotate(180deg)"
    },
    paper: {
        backgroundColor: "#fff",
        padding: 16
    },
    checkIcon: {
        fontSize: 20,
        paddingRight: 4,
        color: "#fff",

    },
    button: {
        padding: 0,
        textTransform: "none"
    }
}))

const JobComplete = forwardRef(({ id, params, ...props }, ref) => {
    const classes = useStyles()
    const { closeSnackbar } = useSnackbar()
    const [expanded, setExpanded] = useState(false)

    const handleExpandClick = useCallback(() => {
        setExpanded(oldExpanded => !oldExpanded)
    }, [])

    const handleDismiss = useCallback(() => {
        closeSnackbar(id)
    }, [id, closeSnackbar])

    return (
        <SnackbarContent ref={ref} className={classes.root}>

            <Card className={classes.card} style={{ backgroundColor: "#57CA22" }}>

                <CardActions >

                    <Stack
                        direction="row"
                        spacing={0.5}
                        sx={{
                            justifyContent: "space-evenly",
                            alignItems: "center",
                        }}
                    >
                        <CheckCircleIcon className={classes.checkIcon} />

                        <Typography
                            sx={{
                                fontWeight: 'bold',
                            }}
                            variant="body2" className={classes.typography}>
                            {props.message}
                        </Typography>

                        <div className={classes.icons}>
                            <IconButton
                                aria-label="Show more"
                                size="small"
                                className={clsx(classes.expand, {
                                    [classes.expandOpen]: expanded
                                })}
                                onClick={handleExpandClick}
                            >
                                <ExpandMoreIcon />
                            </IconButton>
                            <IconButton
                                size="small"
                                className={classes.expand}
                                onClick={handleDismiss}
                            >
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        </div>
                    </Stack>
                </CardActions>

                <Collapse in={expanded} timeout="auto" unmountOnExit>

                    <Paper
                        className={classes.paper}>

                        {objectHas(params, 'title') &&
                            stringExists(params.title) &&
                            <Typography
                                sx={{ 'marginLeft': '10px', 'marginRight': '10px', paddingTop: '10px', paddingBottom: '5px' }}
                                style={{ color: "#000", display: "block" }}
                            >
                                {`${params.title}`}
                            </Typography>
                        }

                        {objectHas(params, 'fileName') &&
                            stringExists(params.fileName) &&
                            <Typography
                                sx={{ 'marginLeft': '10px', 'marginRight': '10px', paddingTop: '5px', paddingBottom: '5px' }}
                                style={{ color: "#000", display: "block" }}
                            >
                                {`${params.fileName}`}
                            </Typography>
                        }

                        {objectHas(params, 'pmId') &&
                            stringExists(params.pmId) &&
                            <Typography
                                sx={{ 'marginLeft': '10px', 'marginRight': '10px', 'paddingBottom': '10px' }}
                                style={{ color: "#000", display: "block" }}
                            >
                                {`PM ID: ${params.pmId}`}
                            </Typography>
                        }

                    </Paper>
                </Collapse>
            </Card>

        </SnackbarContent>
    )
})

JobComplete.displayName = "Job Complete";

export default JobComplete;
